/**
 * Spec 027, D13: «Агент предлагает поправить себя» in «Решить». The feedback's author reads
 * their own words, the agent's reason and the diff of the card, as approval shows it, and
 * decides by hand: «Принять» writes the next version and approves it for their computer,
 * «Поправить и принять» lets them edit the proposed setup first, «Не надо» closes it.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AgentRequests, Agents } from "@/api/resources";
import type { AgentRequestDecision, WaitingItem } from "@/api/types";
import { DiffView } from "@/components/AgentForm";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Caption, EmptyNote } from "@/kit";
import { decidedText } from "@/lib/agents";

export function CardChangeBody({ item }: { item: WaitingItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const requestId = item.request_id!;
  const agentId = item.item.id;
  // The diff comes with its revision: «Принять» decides the revision shown (review of #55).
  const diff = useQuery({ queryKey: ["agent-requests", "diff", requestId], queryFn: () => AgentRequests.diff(requestId) });
  const said = useQuery({ queryKey: ["agents", "feedback", agentId], queryFn: () => Agents.feedback(agentId) });
  const words = (said.data ?? []).filter((f) => f.request_id === requestId);
  const decide = useMutation({
    mutationFn: (body: AgentRequestDecision) => AgentRequests.decide(requestId, body),
    onSuccess: (_, body) => {
      setEditing(false);
      toast.success(decidedText("card_change", body.decision));
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      // Rebuilt meanwhile: show the new revision before anything is decided.
      void qc.invalidateQueries({ queryKey: ["agent-requests", "diff", requestId] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  const revision = diff.data?.revision;
  const proposedSetup = diff.data?.changes.setup_md;
  return (
    <div className="flex flex-col gap-3">
      {words.length > 0 && (
        <section className="space-y-1">
          <Caption>{words.length > 1 ? "Твои отзывы" : "Твой отзыв"}</Caption>
          <ul className="space-y-1 text-body text-p-text">
            {words.map((f) => (
              <li key={f.id} className="break-words">«{f.text}»{f.item_title ? <span className="text-meta text-p-text-muted"> — {f.item_title}</span> : null}</li>
            ))}
          </ul>
        </section>
      )}
      <section className="space-y-1.5">
        <Caption>Что агент поменяет</Caption>
        {diff.isLoading ? <SkeletonList count={2} /> : diff.data ? <DiffView diff={diff.data.diff} /> : <EmptyNote>Не удалось загрузить разницу.</EmptyNote>}
      </section>
      <p className="text-meta text-p-text-muted">«Принять» — новая версия карточки, для твоего компьютера сразу одобрена; идущий запуск перейдёт на неё между единицами работы.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="strong" size="compact" loading={decide.isPending && !editing} disabled={!diff.data}
          onClick={() => decide.mutate({ decision: "grant", revision })}>
          Принять
        </Button>
        {proposedSetup !== undefined && (
          <Button variant="secondary" size="compact" disabled={decide.isPending} onClick={() => setEditing(true)}>Поправить и принять</Button>
        )}
        <Button variant="secondary" size="compact" disabled={decide.isPending || !diff.data} onClick={() => decide.mutate({ decision: "refuse", revision })}>Не надо</Button>
      </div>
      {editing && proposedSetup !== undefined && revision !== undefined && (
        <EditSetup initial={proposedSetup} pending={decide.isPending} onClose={() => setEditing(false)}
          onAccept={(setup) => decide.mutate({ decision: "grant", changes: { setup_md: setup }, revision })} />
      )}
    </div>
  );
}

function EditSetup({ initial, pending, onClose, onAccept }: {
  initial: string;
  pending: boolean;
  onClose: () => void;
  onAccept: (setup: string) => void;
}) {
  const [setup, setSetup] = useState(initial);
  return (
    <Sheet open onClose={onClose} title="Поправить и принять"
      footer={<Button block variant="strong" loading={pending} onClick={() => onAccept(setup)}>Принять с моей правкой</Button>}>
      <div className="flex flex-col gap-3">
        <TextArea label="Установка" hint="Как её предложил агент — поправь и прими. Схему и помощников правь потом в карточке."
          value={setup} rows={12} maxLength={50_000} onChange={(e) => setSetup(e.target.value)} />
      </div>
    </Sheet>
  );
}
